import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Upload, Image } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface LogoUploaderProps {
  currentLogoUrl?: string;
  onLogoUpdate: (logoUrl: string) => void;
}

export function LogoUploader({ currentLogoUrl, onLogoUpdate }: LogoUploaderProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      toast({
        title: "Invalid file type",
        description: t('org.logo.error.invalidType'),
        variant: "destructive"
      });
      return;
    }

    // Validate file size (1MB limit)
    if (file.size > 1024 * 1024) {
      toast({
        title: "File too large",
        description: t('org.logo.error.tooLarge'),
        variant: "destructive"
      });
      return;
    }

    try {
      setUploading(true);
      
      const formData = new FormData();
      formData.append('file', file);

      const { data, error } = await supabase.functions.invoke('org-logo', {
        body: formData
      });

      if (error) throw error;

      onLogoUpdate(data.logo_url);
      
      toast({
        title: "Logo uploaded",
        description: "Firm logo has been updated",
      });

    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: "Upload failed",
        description: t('org.logo.error.uploadFailed'),
        variant: "destructive"
      });
    } finally {
      setUploading(false);
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="space-y-4">
      <Label>{t('org.logo.upload')}</Label>
      
      {currentLogoUrl && (
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">{t('org.logo.current')}:</p>
          <div className="border rounded-lg p-4 bg-muted/20 max-w-xs">
            <img 
              src={currentLogoUrl} 
              alt="Current logo" 
              className="max-h-16 max-w-full object-contain"
            />
          </div>
        </div>
      )}

      <div className="flex items-center gap-4">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/jpg,image/svg+xml"
          onChange={handleFileSelect}
          className="hidden"
        />
        
        <Button
          type="button"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="gap-2"
        >
          {uploading ? (
            <>
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-muted border-t-primary" />
              Uploading...
            </>
          ) : (
            <>
              <Upload className="h-4 w-4" />
              {t('buttons.upload')}
            </>
          )}
        </Button>
      </div>

      <p className="text-xs text-muted-foreground">
        PNG, JPG, SVG up to 1MB
      </p>
    </div>
  );
}